import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import axios from "axios"
import Swal from "sweetalert2"
import { toast } from "react-toastify"
import moment from "moment"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEnvelopeOpenText, faEye, faSearch, faTrash } from "@fortawesome/free-solid-svg-icons"
import { baseUrl } from "../../config/baseUrl"
import empty from "../../assets/images/empty-box.png"
import PermissionGuard from "../PermissionGuard/PermissionGuard"
import { hasPermission, PERMISSIONS } from "../../utils/permissions"

const STATUS_OPTIONS = ["open", "pending", "resolved"]

export default function MessagesList() {
  const canEditContactQuery = hasPermission(PERMISSIONS.CONTACT_QUERY_EDIT)
  const [messages, setMessages] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [loading, setLoading] = useState(false)
  const [statusLoadingId, setStatusLoadingId] = useState("")
  const [deleteLoadingId, setDeleteLoadingId] = useState("")

  useEffect(() => {
    fetchMessages()
  }, [])

  const fetchMessages = async () => {
    try {
      setLoading(true)
      const response = await axios.get(`${baseUrl}/contact-query/all`, {
        headers: {
          Authorization: localStorage.getItem("token"),
        },
      })

      if (response?.data?.status) {
        setMessages(response.data.data || [])
      } else {
        toast.error(response?.data?.message || "Failed to fetch contact queries")
      }
    } catch (error) {
      console.error("Error fetching contact queries:", error)
      toast.error("Failed to fetch contact queries")
    } finally {
      setLoading(false)
    }
  }

  const handleStatusChange = async (id, nextStatus) => {
    try {
      setStatusLoadingId(id)
      const response = await axios.patch(
        `${baseUrl}/contact-query/${id}/status`,
        { status: nextStatus },
        {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        },
      )

      if (response?.data?.status) {
        setMessages((prev) =>
          prev.map((message) =>
            message._id === id ? { ...message, status: nextStatus } : message,
          ),
        )
        toast.success("Status updated successfully")
      } else {
        toast.error(response?.data?.message || "Failed to update status")
      }
    } catch (error) {
      console.error("Error updating query status:", error)
      toast.error(error.response?.data?.message || "Failed to update status")
    } finally {
      setStatusLoadingId("")
    }
  }

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete query?",
      text: "This contact query will be removed permanently.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it",
    })

    if (!result.isConfirmed) return

    try {
      setDeleteLoadingId(id)
      const response = await axios.delete(`${baseUrl}/contact-query/${id}`, {
        headers: {
          Authorization: localStorage.getItem("token"),
        },
      })

      if (response?.data?.status) {
        setMessages((prev) => prev.filter((message) => message._id !== id))
        toast.success("Contact query deleted successfully")
      } else {
        toast.error(response?.data?.message || "Failed to delete contact query")
      }
    } catch (error) {
      console.error("Error deleting contact query:", error)
      toast.error(error.response?.data?.message || "Failed to delete contact query")
    } finally {
      setDeleteLoadingId("")
    }
  }

  const truncateMessage = (message) => {
    if (!message) return "N/A"
    return message.length > 90 ? `${message.slice(0, 90)}...` : message
  }

  const normalizedSearchTerm = searchTerm.trim().toLowerCase()
  const filteredMessages = messages.filter((message) => {
    const effectiveStatus = message?.status || "open"
    const matchesStatus = !statusFilter || effectiveStatus === statusFilter

    if (!normalizedSearchTerm) {
      return matchesStatus
    }

    const matchesSearch = [message?.name, message?.email, message?.subject, message?.message, effectiveStatus].some(
      (field) => String(field ?? "").toLowerCase().includes(normalizedSearchTerm),
    )

    return matchesStatus && matchesSearch
  })

  return (
    <section className="main-sec">
      <div className="container-fluid">
        <div className="row">
          <div className="col-lg-6">
            <div className="dashboard-title">
              <h4 className="dash-head">
                <FontAwesomeIcon icon={faEnvelopeOpenText} className="me-2" />
                Contact Queries
              </h4>
            </div>
            <div className="custom-bredcump">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Contact Queries
                  </li>
                </ol>
              </nav>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-lg-12">
            <div className="cards bus-list">
              <div className="bus-filter">
                <div className="row align-items-center gy-2">
                  <div className="col-lg-6">
                    <h5 className="card-title mb-0">
                      Query List ({filteredMessages.length}
                      {normalizedSearchTerm || statusFilter ? ` of ${messages.length}` : ""})
                    </h5>
                  </div>
                </div>
              </div>

              <div className="custom-search-filter mb-4">
                <div className="row gy-3 gx-md-3 gx-2 align-items-center">
                  <div className="col-12 col-md-6 col-lg-5">
                    <div className="position-relative">
                      <FontAwesomeIcon
                        icon={faSearch}
                        className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"
                      />
                      <input
                        type="text"
                        className="form-control ps-5"
                        placeholder="Search by name, email, subject..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-6 col-lg-3">
                    <select
                      className="form-select"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="">All Status</option>
                      <option value="open">Open</option>
                      <option value="pending">Pending</option>
                      <option value="resolved">Resolved</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="table-responsive custom-table">
                <table className="table table-borderless align-middle">
                  <thead>
                    <tr>
                      <th>SN</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Subject</th>
                      <th>Message</th>
                      <th>Status</th>
                      <th>Submitted At</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="8" className="loading-state">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <p className="mt-2 mb-0">Loading contact queries...</p>
                        </td>
                      </tr>
                    ) : filteredMessages.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="empty-state">
                          <img src={empty || "/placeholder.svg"} alt="No queries" width="180" />
                          <h4>{normalizedSearchTerm || statusFilter ? "No matching queries found!" : "No contact queries yet!"}</h4>
                          <p className="text-muted mb-0">
                            {normalizedSearchTerm || statusFilter
                              ? "Try adjusting your search or filter criteria."
                              : "Queries submitted from the website will appear here."}
                          </p>
                        </td>
                      </tr>
                    ) : (
                      filteredMessages.map((message, index) => {
                        const effectiveStatus = message?.status || "open"
                        const isStatusLoading = statusLoadingId === message?._id
                        const isDeleteLoading = deleteLoadingId === message?._id

                        return (
                          <tr key={message?._id}>
                            <td>{index + 1}</td>
                            <td>
                              <div className="fw-semibold">{message?.name || "N/A"}</div>
                            </td>
                            <td>
                              {message?.email ? (
                                <a href={`mailto:${message.email}`} className="text-decoration-none">
                                  {message.email}
                                </a>
                              ) : (
                                "N/A"
                              )}
                            </td>
                            <td>{message?.subject || "N/A"}</td>
                            <td>
                              <div className="message-cell">{truncateMessage(message?.message)}</div>
                            </td>
                            <td>
                              <select
                                className={`form-select form-select-sm status-select status-${effectiveStatus}`}
                                value={effectiveStatus}
                                onChange={(e) => handleStatusChange(message?._id, e.target.value)}
                                disabled={isStatusLoading || !canEditContactQuery}
                              >
                                {STATUS_OPTIONS.map((status) => (
                                  <option key={status} value={status}>
                                    {status.charAt(0).toUpperCase() + status.slice(1)}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              {message?.createdAt ? moment(message.createdAt).format("DD MMM YYYY, hh:mm A") : "N/A"}
                            </td>
                            <td>
                              <div className="action-buttons">
                                <Link
                                  to={`/contact-queries/view/${message?._id}`}
                                  className="action-btn view-btn"
                                  title="View Query"
                                >
                                  <FontAwesomeIcon icon={faEye} />
                                </Link>
                                <PermissionGuard permission={PERMISSIONS.CONTACT_QUERY_DELETE}>
                                  <button
                                    className="action-btn delete-btn"
                                    title="Delete Query"
                                    onClick={() => handleDelete(message?._id)}
                                    disabled={isDeleteLoading}
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </button>
                                </PermissionGuard>
                              </div>
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .message-cell {
          min-width: 260px;
          max-width: 340px;
          color: #495057;
          white-space: normal;
          line-height: 1.5;
        }

        .status-select {
          min-width: 120px;
          font-weight: 600;
          border-radius: 999px;
          border-width: 0;
          box-shadow: none !important;
        }

        .status-open {
          background-color: #dbeafe;
          color: #1d4ed8;
        }

        .status-pending {
          background-color: #fef3c7;
          color: #b45309;
        }

        .status-resolved {
          background-color: #d1fae5;
          color: #047857;
        }

        .action-buttons {
          display: flex;
          gap: 10px;
        }

        .action-btn {
          width: 34px;
          height: 34px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          text-decoration: none;
        }

        .view-btn {
          background-color: #e0f2fe;
          color: #0284c7;
        }

        .view-btn:hover {
          background-color: #0284c7;
          color: white;
        }

        .delete-btn {
          background-color: #f8d7da;
          color: #dc3545;
        }

        .delete-btn:hover:not(:disabled) {
          background-color: #dc3545;
          color: white;
        }

        .loading-state,
        .empty-state {
          text-align: center;
          padding: 40px 20px;
        }

        .empty-state img {
          margin-bottom: 16px;
        }

        .empty-state h4 {
          margin-bottom: 10px;
          color: #343a40;
        }
      `}</style>
    </section>
  )
}
